import { computed, observe } from 'mobx';
import Collection from 'core/model/Collection';
import { ViewCollection } from 'app/stores/util/ViewCollection';
import { showWarningToast } from 'core/components';
import Otp from './Otp';

const isWebAuthn = (otp) => otp.get('type') === 'webauthn';
const isCrossPlatformWebAuthn = (otp) =>
  isWebAuthn(otp) && otp.get('webauthn_authenticator_attachment') === 'cross-platform';
const isPlatformWebAuthn = (otp) =>
  isWebAuthn(otp) && otp.get('webauthn_authenticator_attachment') !== 'cross-platform';

export default class OtpCollection extends Collection {
  get url() {
    return '/api/ui/totp';
  }

  get model() {
    return Otp;
  }

  constructor() {
    super();

    this._warningReactionDisposer = observe(this, 'hasPortableAuthenticator', ({ oldValue, newValue }) => {
      if (this.enabledSize && newValue === false && oldValue === true) {
        showWarningToast(
          'Your authentication methods are bound to this device. For access on other computers, please register a cross-platform security key.'
        );
      }
    });
  }

  destroy() {
    if (this._warningReactionDisposer) {
      this._warningReactionDisposer();
    }
  }

  @computed
  get hasEnabledOtp() {
    return this.models.some((otp) => otp.get('enabled'));
  }

  @computed
  get platformOtps() {
    return new ViewCollection(this.get().filter(isPlatformWebAuthn), this);
  }

  @computed
  get crossPlatformOtps() {
    return new ViewCollection(this.get().filter(isCrossPlatformWebAuthn), this);
  }

  @computed
  get legacyOtps() {
    return new ViewCollection(
      this.get().filter((otp) => !isWebAuthn(otp)),
      this
    );
  }

  @computed
  get enabledSize() {
    return this.get().filter((otp) => otp.get('enabled')).length;
  }

  @computed
  get hasPortableAuthenticator() {
    return !!(
      // legacy OTPs (TOTP codes from authenticator apps) can always be used as they are not tied to a specific device
      (
        this.legacyOtps.get().some((otp) => otp.get('enabled')) ||
        // cross platform OTPs can always be used as they are external to the current device
        this.crossPlatformOtps.get().some((otp) => otp.get('enabled')) ||
        // platform OTPs can be used ONLY if they are multi-device
        this.platformOtps.get().some((otp) => otp.get('enabled') && otp.get('webauthn_type') === 'multiDevice')
      )
    );
  }
}
